<script setup>
import { ref, computed } from 'vue'
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { XMarkIcon } from '@heroicons/vue/20/solid'

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
  },
  type: {
    type: String,
  },
})

const DEFAULT_TITLES = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  preview: 'Preview',
}

const displayTitle = computed(() => props.title || DEFAULT_TITLES[props.type] || '')

const show = ref(true)
const alertType = ref(props.type)
const alertClass = ref('')

switch (alertType.value) {
  case 'success':
    alertClass.value = 'pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5'
    break
  case 'error':
    alertClass.value = 'pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-red-50 shadow-lg ring-1 ring-black/5'
    break
  case 'warning':
    alertClass.value = 'pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-yellow-100 shadow-lg ring-1 ring-black/5'
    break
  case 'preview':
    alertClass.value = 'pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-yellow-100 shadow-lg ring-1 ring-black/5'
    break
}
</script>
<template>
  <!-- Global notification live region, render this permanently at the end of the document -->
  <div aria-live="assertive" class="pointer-events-none fixed w-full z-50 bottom-10 right-0 flex items-end px-4 py-6 sm:items-start sm:p-6">
    <div class="flex w-full flex-col items-center space-y-4 sm:items-end">
      <!-- Notification panel, dynamically insert this into the live region when it needs to be displayed -->
      <transition
        enter-active-class="transform ease-out duration-300 transition"
        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="show" :class="alertClass">
          <div class="p-4">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <CheckCircleIcon v-if="type == 'success'" class="h-6 w-6 text-green-400" aria-hidden="true" />
                <XCircleIcon v-if="type == 'error'" class="h-6 w-6 text-red-400" aria-hidden="true" />
                <ExclamationTriangleIcon v-if="type == 'warning' || type == 'preview'" class="h-6 w-6 text-yellow-600" aria-hidden="true" />
              </div>
              <div class="ml-3 w-0 flex-1 pt-0.5">
                <p class="text-sm font-medium text-gray-900">{{ displayTitle }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ props.message }}</p>
              </div>
              <div class="ml-4 flex flex-shrink-0">
                <button
                  type="button"
                  class="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  @click="show = false"
                >
                  <span class="sr-only">Close</span>
                  <XMarkIcon class="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
